import React, { useRef, useMemo, useState, useEffect, useContext } from 'react';
import { history, useIntl, getLocale } from 'umi';
import { Bubble, Icon, Modal } from 'tea-component';
import SvgIcon from '@home/components/SvgIcon';
import GameImage from '@home/components/GameImage';
import CreatorBox from './CreatorBox';
import type { IReportAuthorViewModel } from './authors';
import AggregationBubble from '@intelligence/pages/TopCharts/components/DetailsPage/AggregationBubble';
import useData from './_hook';
import { getReportLangForFile } from '@intelligence/utils/reportLanguage';
import { getCurrentRoutePath } from '@intelligence/utils/commonUtils';
import { getReportResourcePath } from '@intelligence/utils/reports';
import { getViewReportApi, setLikeInsightsReport } from '@intelligence/services/reportService';
import { openPDFPreviewDownloadPage, getReportPreviewFileName } from './utils';
import { buriedBtnPoint } from '@home/utils/buriedPoint';
import { dbiBtnLogMes } from '@intelligence/utils/buriedPointCfg';
import { formatReportLike, pageReportTypeConfig } from '@intelligence/pages/InsightsReport/_config';
import { StudioContext } from '@home/pages/studio/context';

import ExpandableSummary from './ExpandableSummary';
import './index.scss';

import LikeBtn from '@intelligence/assets/svg/tipicon/like-new.svg';
import ViewBtn from '@intelligence/assets/svg/tipicon/view.svg';

interface IReportData {
  id: string;
  resource_id: string;
  title: string[] | string;
  originTitle?: string[] | string;
  watched_cnt?: number;
  report_like?: number;
  summary?: string[] | string;
  tag?: string[] | string;
  coverImg?: string;
  authors: IReportAuthorViewModel[];
  time?: string;
  report_type?: string[];
  file_name?: string;
  pdf?: string;
  pdf_en?: string;
}

interface IInsightsReportCardProps {
  data: IReportData;
  isNew?: boolean;
  lang: number;
  shareBack: string;
  isShowCreatorBox?: boolean;
  reportTypeConfig: any;
  pdfPreviewDownloadPage: string;
  onChange: (resourceId: string, likeCounts?: number) => void;
  onViewsChange?: (resourceId: string, viewCounts?: number) => void;
  showContactInfo?: boolean;
  // 页面真实的报告语种segment的value，不经过任何逻辑转换的
  reportLangSegment: string;
  downloadLogConfigKey?: string;
  onCopyIconClick?: (email: string) => void;
  topRightRender?: () => React.ReactNode;
  badgeRender?: () => React.ReactNode;
  /** 摘要超过 3 行显示省略号 + More，点击展开；开启后不展示 Bubble */
  summaryExpandable?: boolean;
  needPopover?: boolean;
}

export default function InsightsReportCard(props: IInsightsReportCardProps) {
  const {
    reportLangSegment = '',
    topRightRender,
    badgeRender,
    summaryExpandable = false,
    needPopover: needPopoverProp,
  } = props;

  const { formatMessage } = useIntl();

  const locale = getLocale();

  const {
    getReportShow,
    reportUrl,
    reportLoading,
    run,
    onLikeClick,
    onShareClick,
    shareBubble,
    likeBubble,
    likeCnt,
    setLikeCnt,
    setLikeBubble,
    downloadReports,
    needPopover: needPopoverFromHook,
  } = useData(props);
  const needPopover = summaryExpandable
    ? false
    : needPopoverProp ?? needPopoverFromHook;

  const {
    data = { title: '', id: '', resource_id: '', authors: [] },
    isNew,
    lang = 0,
    shareBack = '',
    isShowCreatorBox = true,
    reportTypeConfig,
    pdfPreviewDownloadPage = '/pdfPreviewDownloadLikeShare',
    onChange,
    onViewsChange,
    showContactInfo,
    downloadLogConfigKey = 'insightsReportsDownload',
    onCopyIconClick,
  }: IInsightsReportCardProps = props;

  /**
   * 目前浏览量计数有 studio report、insight report、iic report、game research report
   * studio report 按照原有逻辑使用 getViewReportApi 接口
   * insight report、iic report、game research report 使用 setLikeInsightsReport（点赞转发浏览量计数） 接口
   */
  const isInsightIccGameResearchReport = [
    pageReportTypeConfig.insightsReports.systemIdCur,
    pageReportTypeConfig.gameResearchReport.systemIdCur,
    pageReportTypeConfig.insightsCenterLibrary.systemIdCur,
  ].includes(reportTypeConfig.systemIdCur);

  const { currentGame } = useContext(StudioContext);
  const { studio_code: studioId } = currentGame || {};

  // 报告的语种：单语言直跳唯一文件，多语言按 locale 选择
  const reportLang = useMemo<'en' | 'cn'>(() => {
    return getReportLangForFile(data, reportLangSegment, locale);
  }, [data, reportLangSegment, locale]);

  const [viewCnt, setViewCnt] = useState(data?.watched_cnt || 0);
  const { systemIdCur, downloadLogKeyCur, pagePathCur, showLiked, showView, countView, likedClickable } =
    reportTypeConfig || {};
  const [showDetail, setShowDetail] = useState(false);

  const [tagList, setTagList] = useState<any>({ top: [], res: [] });

  const reportTitle = Array.isArray(data?.title) ? data?.title?.[lang] : data?.title;
  const originReportTitle = Array.isArray(data?.originTitle) ? data?.originTitle?.[lang] : data?.originTitle;

  const currentReportTitle = originReportTitle || reportTitle;
  const reportSummary = Array.isArray(data?.summary) ? data?.summary?.[lang] : data?.summary;
  const reportTag = Array.isArray(data?.tag) ? data?.tag?.[lang] : data?.tag;

  // 收集后端响应返回的点赞数，取用最新点赞数，避免请求竟态问题先发后至导致点赞数非最新
  const likeRequestDataRef = useRef<number[]>([]);

  useEffect(() => {
    // 过滤掉报告为空字符的值
    const top = Array.isArray(reportTag) ? reportTag?.filter((t) => t) : [];
    const res = top.splice(5);
    setTagList({
      top,
      res,
    });
    // const cur = likeCnt > 0 ? likeCnt : data.report_like;
    // setLikeCnt(cur);
    if (shareBack && shareBack === data.resource_id) {
      getReportShow('intelligence_insights', data.resource_id, reportLang);
      setShowDetail(true);
    }
  }, [lang, data]);

  const handleView = async (id: string, resourceId: string) => {
    try {
      const viewData = await (() => {
        if (isInsightIccGameResearchReport) {
          return setLikeInsightsReport({
            system: reportTypeConfig.systemIdCur,
            resource_id: resourceId || '',
            action_type: 'watch',
          });
        }

        return getViewReportApi({
          system: reportTypeConfig.systemIdCur,
          resource_id: resourceId || '',
          id,
        });
      })();

      onViewsChange?.(resourceId, viewData);
      setViewCnt(viewData);
    } catch (e) {
      console.log(e);
    }
  };

  const clickLikeBtn = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (!likedClickable) return;

    try {
      // 先前端模拟点赞
      onChange?.(data.resource_id);
      setLikeCnt((counts: number) => {
        const newCount = counts + 1;
        likeRequestDataRef.current.push(newCount);
        return newCount;
      });

      // 发起请求
      const likeCounts = await onLikeClick(data.resource_id, reportTypeConfig.systemIdCur);

      // 取出最大的点赞数（页面+请求），规避高频请求竟态及前后端不同步问题
      likeRequestDataRef.current.push(likeCounts);
      const latestLikeCounts = Math.max(...likeRequestDataRef.current);

      // 取用前后端整合数据
      setLikeCnt(latestLikeCounts);
      onChange?.(data.resource_id, latestLikeCounts);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <>
      <Modal className="dbi-reportcard-modal" disableCloseIcon visible={showDetail}>
        <div className="tea-dialog__header">
          <h3 className="tea-dialog__headertitle">{reportTitle}</h3>
          <div className="dbi-reportcard-modal-iconbox">
            <div
              className="dbi-reportcard-modal-download dbi-reportcard-modal-icon"
              onClick={() => {
                if (data?.resource_id) {
                  downloadReports(data.resource_id);
                  // @ts-ignore
                  const BtnLogMes = dbiBtnLogMes[downloadLogConfigKey];
                  // BtnLogMes.extInfo = reportTitle;
                  BtnLogMes.extInfo = currentReportTitle;
                  buriedBtnPoint(BtnLogMes);
                }
              }}
            >
              <SvgIcon type="download" />
            </div>
            <div
              className="dbi-reportcard-modal-close dbi-reportcard-modal-icon"
              onClick={() => {
                setShowDetail(!showDetail);
                setLikeBubble(false);

                // 调用者有配置返回页面路由就返回指定的页面路由，否则只需要关闭弹窗即可
                if (pagePathCur) {
                  history.push(pagePathCur);
                }
              }}
            >
              <SvgIcon type="clonse" />
            </div>
          </div>
        </div>
        <Modal.Body>
          <div className="dbi-reportcard-modal-pdf">
            {/* pdf 示意*/}
            <div className={reportLoading ? 'report-loading' : 'report-loading-none'}>
              <Icon size="l" type="loading" />
              &nbsp;&nbsp;&nbsp;{formatMessage({ id: 'dbi_reports_loading' })}
            </div>
            {!reportLoading && <iframe className="dbi-reportcard-modal-pdfinner" src={reportUrl}></iframe>}
            {/* 点赞分享 */}
            <div className={reportLoading ? 'report-loading-none' : 'dbi-reportcard-modal-footer'}>
              {/* 浏览量 */}
              {showView && (
                <div className="report-content-operate-item">
                  <div className="report-content-operate-item-round report-view">
                    <SvgIcon type="open-eyes" />
                    {/* <img src={ViewBtn} alt="" /> */}
                  </div>
                  <div className="like-box">
                    <div className="like-after">{formatMessage({ id: 'dbh_studio_report_view' })}</div>
                    <span>{formatReportLike(viewCnt)}</span>
                  </div>
                </div>
              )}
              {showLiked && (
                <div className="report-content-operate-item">
                  <Bubble dark trigger="click" visible={likeBubble} content="Thank you for your feedback!">
                    <div
                      className="report-content-operate-item-round report-like"
                      onClick={() => {
                        run(data.resource_id);
                        onChange?.(data.resource_id);
                      }}
                    >
                      <SvgIcon type="very-good" />
                      {/* <img src={LikeReportBtn} alt="" /> */}
                    </div>
                    <div className="like-box">
                      <div className="like-after">{formatMessage({ id: 'dbi_insights_report_like' })}</div>
                      <span>{formatReportLike(data?.report_like)}</span>
                    </div>
                  </Bubble>
                </div>
              )}
              <div className="report-content-operate-item">
                <Bubble dark trigger="click" visible={shareBubble} content="Link has been successfully copied!">
                  <div
                    className="report-content-operate-item-round report-share"
                    onClick={() => {
                      onShareClick(data.resource_id);
                    }}
                  >
                    <SvgIcon type="aigc-share" />
                  </div>
                  <div>{formatMessage({ id: 'dbi_insights_report_share' })}</div>
                </Bubble>
              </div>
            </div>
          </div>
        </Modal.Body>
      </Modal>
      <div
        className="dbi-reportcard"
        onClick={() => {
          /**
           * 浏览量计数逻辑
           * 1. 展示 view 浏览量内容时
           * 2. 不展示 view 浏览量但需要浏览量计数时
           */
          if ((showView || countView) && data?.id && data?.resource_id) {
            // 请求view浏览接口
            handleView(data?.id, data?.resource_id);
          }

          const resourcePath = getReportResourcePath(locale, { ...data, lang: reportLang });
          const isStudioReportCard =
            systemIdCur === pageReportTypeConfig.studioReport.systemIdCur;
          let openParms = {
            id: data?.id,
            systemId: systemIdCur,
            resourceId: data.resource_id,
            // resourceName: reportTitle,
            resourceName: currentReportTitle,
            resourcePath,
            resourceLike: data?.report_like,
            downloadLogKey: downloadLogKeyCur,
            lang: reportLang,
            referer: !!studioId ? '' : getCurrentRoutePath(),
            ...(isStudioReportCard
              ? { fileName: getReportPreviewFileName(resourcePath, data) }
              : {}),
          } as any;

          // 如果是studio页面的报告，才加studio
          if (studioId) {
            openParms = {
              ...openParms,
              studioId,
            };
          }

          if (data?.resource_id) {
            // 打点转移到打开的报告页面
            openPDFPreviewDownloadPage(pdfPreviewDownloadPage, openParms);
          }
        }}
      >
        {topRightRender?.()}
        <div style={{ width: isShowCreatorBox ? '70%' : '100%', display: 'flex' }}>
          {/* <img
            className="dbi-reportcard-img"
            src={data.coverImg == 'error' ? '' : data.coverImg}
          /> */}
          <GameImage
            className={'dbi-reportcard-img'}
            // src={'doverImg'}
            src={data.coverImg == 'error' ? '' : data.coverImg}
            alt={'flag'}
            entityType={'report'}
          />
          <div style={{ flex: 1, paddingLeft: 20 }}>
            <div className="dbi-reportcard-title insights-title">
              <div className="dbi-reportcard-tit insights-tit">
                <span dangerouslySetInnerHTML={{ __html: reportTitle }} />
              </div>
              {isNew ? <div className="dbi-reportcard-notice">NEW</div> : null}
            </div>
            {badgeRender?.()}
            {summaryExpandable ? (
              <ExpandableSummary
                html={reportSummary || ''}
                className="dbi-reportcard-des insights-des"
                maxLines={3}
              />
            ) : (
              <div className="dbi-reportcard-des insights-des">
                {needPopover ? (
                  <AggregationBubble
                    AWidth={550}
                    DomOrText={
                      <div dangerouslySetInnerHTML={{ __html: reportSummary || '' }} />
                    }
                    customBubbleContent={
                      <span dangerouslySetInnerHTML={{ __html: reportSummary || '' }} />
                    }
                    hasPointer={false}
                  />
                ) : (
                  <span
                    className="dbi-reportcard-des dbi-reportcard-new"
                    dangerouslySetInnerHTML={{ __html: reportSummary || '' }}
                  />
                )}
              </div>
            )}
            <div className="dbi-reportcard-info">
              {data.time && (
                <span className="dbi-reportcard-time" dangerouslySetInnerHTML={{ __html: data.time }}></span>
              )}
              {data.report_type?.[lang] && (
                <span
                  className="dbi-reportcard-report-type"
                  dangerouslySetInnerHTML={{ __html: data.report_type[lang] }}
                ></span>
              )}
              {showView && (
                <span className="dbi-reportcard-view">
                  <img src={ViewBtn} alt="" />
                  {/* <SvgIcon type='open-eyes' /> */}
                  <span>{`${formatMessage({ id: 'dbh_studio_report_view' })} ${formatReportLike(viewCnt)}`}</span>
                </span>
              )}
              {showLiked && (
                <span
                  className="dbi-reportcard-view"
                  onClick={(e) => {
                    clickLikeBtn(e);
                  }}
                >
                  <Bubble
                    dark
                    trigger="click"
                    visible={likeBubble}
                    updateDeps={[data?.report_like]}
                    content={formatMessage({ id: 'dbi_insight_report_feedback_tips' })}
                  >
                    <img src={LikeBtn} alt="" />
                  </Bubble>

                  <span>
                    {`${formatMessage({ id: 'dbi_reports_liked' })} ${formatReportLike(
                      // data?.report_like,
                      likeCnt,
                    )}`}
                  </span>
                </span>
              )}
            </div>
            {reportTag ? (
              <div className="dbi-reportcard-tagbox">
                {tagList.top?.map((item: any, index: any) => (
                  <span className="dbi-reportcard-label-item" key={index}>
                    <span dangerouslySetInnerHTML={{ __html: item }} />
                  </span>
                ))}
                {tagList.res?.length > 0 ? (
                  <Bubble
                    style={{ maxWidth: 500 }}
                    dark
                    content={<span dangerouslySetInnerHTML={{ __html: tagList.res.join(', ') }} />}
                  >
                    <span className="dbi-reportcard-label-item">{`+${tagList.res.length}`}</span>
                  </Bubble>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
        {isShowCreatorBox && (
          <div style={{ width: '30%' }}>
            <CreatorBox
              authors={data.authors}
              lang={lang}
              showContactInfo={showContactInfo}
              onCopyIconClick={onCopyIconClick}
            />
          </div>
        )}
      </div>
    </>
  );
}
